// MARK://dhk8/pos/components/SearchSelect
// Combobox buscable reutilizable (touch-first, ≥44px). Un input con dropdown
// filtrable por texto: el usuario puede ELEGIR de la lista (autollena vía
// onSelect) O dejar TEXTO LIBRE (onText) para algo no inventariado. El valor
// visible lo controla el padre (`value`) — este componente no guarda estado del
// texto, sólo el abierto/cerrado y el resaltado del teclado.
//
// Estilos con tokens --pos-*/design tokens como el resto del POS; el <style> se
// inyecta una sola vez por página (idempotente aunque se monten varios).
import { useEffect, useMemo, useRef, useState } from 'react'

export interface SearchSelectOption {
  value: string
  label: string
  /** Texto secundario (unidad/costo, RFC…) — sólo informativo. */
  hint?: string
}

export function SearchSelect(props: {
  value: string
  options: SearchSelectOption[]
  onSelect: (opt: SearchSelectOption) => void
  onText: (text: string) => void
  placeholder?: string
  disabled?: boolean
  loading?: boolean
  emptyText?: string
  className?: string
}) {
  const { value, options, onSelect, onText, placeholder, disabled, loading, emptyText, className } = props
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    const term = value.trim().toLowerCase()
    const pool = term.length === 0
      ? options
      : options.filter((o) => o.label.toLowerCase().includes(term) || (o.hint?.toLowerCase().includes(term) ?? false))
    return pool.slice(0, 50)
  }, [value, options])

  // Reajusta el resaltado a un índice válido cuando cambia la lista filtrada.
  useEffect(() => { setHighlight((h) => (h >= filtered.length ? 0 : h)) }, [filtered.length])

  // Cierra al hacer click fuera.
  useEffect(() => {
    if (!open) return
    function onDocDown(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocDown)
    return () => document.removeEventListener('mousedown', onDocDown)
  }, [open])

  function choose(opt: SearchSelectOption): void {
    onSelect(opt)
    setOpen(false)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault(); setOpen(true)
      setHighlight((h) => Math.min(filtered.length - 1, h + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(0, h - 1))
    } else if (e.key === 'Enter') {
      if (open && filtered[highlight]) { e.preventDefault(); choose(filtered[highlight]) }
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={rootRef} className={`dhk8-ss${className ? ` ${className}` : ''}`}>
      <input
        className="dhk8-ss__input"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        onChange={(e) => { onText(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && !disabled && (
        <div className="dhk8-ss__pop" role="listbox">
          {filtered.length === 0 ? (
            <div className="dhk8-ss__empty">{loading ? 'Cargando…' : (emptyText ?? 'Sin coincidencias — se usará el texto tal cual')}</div>
          ) : (
            filtered.map((o, i) => (
              <button
                type="button"
                key={o.value}
                role="option"
                aria-selected={i === highlight}
                className={`dhk8-ss__opt${i === highlight ? ' dhk8-ss__opt--hl' : ''}`}
                // preventDefault en mousedown evita el blur del input antes del click.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => choose(o)}
              >
                <span className="dhk8-ss__opt-label">{o.label}</span>
                {o.hint && <span className="dhk8-ss__opt-hint">{o.hint}</span>}
              </button>
            ))
          )}
        </div>
      )}
      <style>{SS_STYLES}</style>
    </div>
  )
}

const SS_STYLES = `
  .dhk8-ss { position:relative; width:100%; }
  .dhk8-ss__input { width:100%; min-height:44px; border:1px solid var(--separator-opaque); padding:var(--space-2) var(--space-3); background:var(--bg-canvas); color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-sm); outline:none; border-radius:var(--radius-sm); }
  .dhk8-ss__input:focus { border-color:var(--tint); }
  .dhk8-ss__input:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-ss__pop { position:absolute; top:calc(100% + 4px); left:0; right:0; z-index:200; max-height:260px; overflow-y:auto; background:var(--bg-elevated); border:1px solid var(--separator-opaque); border-radius:var(--radius-sm); box-shadow:var(--shadow-lg); display:flex; flex-direction:column; padding:var(--space-1); }
  .dhk8-ss__empty { padding:var(--space-2) var(--space-3); font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-tertiary); }
  .dhk8-ss__opt { display:flex; justify-content:space-between; align-items:center; gap:var(--space-3); min-height:44px; padding:var(--space-2) var(--space-3); border:none; background:transparent; color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-sm); text-align:left; cursor:pointer; border-radius:var(--radius-sm); width:100%; }
  .dhk8-ss__opt--hl, .dhk8-ss__opt:hover { background:var(--tint-bg); }
  .dhk8-ss__opt-label { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .dhk8-ss__opt-hint { flex:0 0 auto; color:var(--text-tertiary); font-size:var(--text-xxs); font-variant-numeric:tabular-nums; }
`
